/**
 * Mode lecture seule sur mobile pour les non-admins.
 * - Détecte si on est sur un écran mobile (< 768px) ou User-Agent mobile.
 * - Si l'utilisateur n'est PAS admin → bloque toutes les actions de modification.
 * - Affiche un banner "Lecture seule" et toast au clic sur un bouton bloqué.
 */

import axios from 'axios';
import { toast } from 'sonner';

const MOBILE_BREAKPOINT_PX = 768;
const STORAGE_USER_KEY = 'bigdem_current_user_role';

let _isMobile = false;
let _userRole = null;
const _listeners = new Set();

function _detectMobile() {
    if (typeof window === 'undefined') return false;
    const w = window.innerWidth || document.documentElement.clientWidth || 0;
    if (w > 0 && w < MOBILE_BREAKPOINT_PX) return true;
    // Fallback user-agent (utile pour les tablettes pliantes en mode portrait)
    const ua = (navigator.userAgent || '').toLowerCase();
    return /android.*mobile|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua);
}

function _emit() {
    _listeners.forEach((cb) => { try { cb(isReadOnly()); } catch { /* ignore */ } });
}

export function isReadOnly() {
    // Mode lecture seule mobile retiré : les permissions par module (via
    // ReadOnlyGuard + le catalogue de permissions) gerent finement les droits
    // d'ecriture par role et par module. Bloquer toutes les requetes
    // d'ecriture sur tablette/telephone empechait les commerciaux/assistantes
    // de modifier leurs visites/inventaires depuis ces appareils.
    return false;
}

export function getCurrentRole() {
    return _userRole;
}

export function setCurrentRole(role) {
    _userRole = role || null;
    try {
        if (role) localStorage.setItem(STORAGE_USER_KEY, role);
        else localStorage.removeItem(STORAGE_USER_KEY);
    } catch { /* ignore */ }
    _emit();
}

export function subscribeReadOnly(cb) {
    _listeners.add(cb);
    cb(isReadOnly());
    return () => _listeners.delete(cb);
}

// Au chargement, restaure le rôle depuis localStorage (en attendant que AuthContext le set)
if (typeof window !== 'undefined') {
    try {
        const cached = localStorage.getItem(STORAGE_USER_KEY);
        if (cached) _userRole = cached;
    } catch { /* ignore */ }

    _isMobile = _detectMobile();
    window.addEventListener('resize', () => {
        const newVal = _detectMobile();
        if (newVal !== _isMobile) {
            _isMobile = newVal;
            _emit();
        }
    });
}

// ─────────────────── Axios interceptor : bloque les writes en mode readonly ───────────────────
function _isWriteRequest(config) {
    const m = (config.method || 'get').toLowerCase();
    return m === 'post' || m === 'put' || m === 'patch' || m === 'delete';
}

axios.interceptors.request.use((config) => {
    if (!isReadOnly()) return config;
    if (!_isWriteRequest(config)) return config;
    const url = config.url || '';
    // Autorise auth endpoints (login/logout/refresh)
    if (url.includes('/api/auth/')) return config;
    // Annule la requête silencieusement (pas de toast)
    const cancelError = new Error('READONLY_MODE');
    cancelError.config = config;
    cancelError.isReadOnly = true;
    return Promise.reject(cancelError);
});
